import { useParams, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Calendar,
  FileText,
  Loader2,
  CheckCircle2,
  Rocket,
  XCircleIcon,
} from "lucide-react";
import { format } from "date-fns";
import { AuditPlanNotFound } from "./plan-detail-components/audit-plan-not-found";
import { useQuery } from "@tanstack/react-query";
import { getAuditPlanById } from "@/lib/audit-plan";
import { AuditPlanActions, OnActionCompletedFn } from "./_components/audit-plan-actions";
import { AuditPlanOverview } from "./plan-detail-components/audit-plan-overview";
import { AssignmentsList } from "./plan-detail-components/assignments-list";

const statusConfig: Record<
  string,
  {
    icon: React.ElementType;
    color: string;
    badgeVariant: "default" | "secondary" | "destructive" | "outline";
  }
> = {
  Draft: { icon: FileText, color: "text-gray-500", badgeVariant: "outline" },
  Active: { icon: Rocket, color: "text-sky-600", badgeVariant: "default" },
  "In Progress": { icon: Rocket, color: "text-sky-600", badgeVariant: "default" },
  Completed: { icon: CheckCircle2, color: "text-green-600", badgeVariant: "default" },
  Cancelled: { icon: XCircleIcon, color: "text-red-600", badgeVariant: "destructive" },
};

export default function AuditPlanDetailPage() {
  const { planId } = useParams({ from: '/audit-plans/$planId' });
  const navigate = useNavigate();

  if (!planId) {
    return <AuditPlanNotFound />;
  }

  const {
    isPending,
    isError,
    data: auditPlan,
  } = useQuery({
    queryKey: ["auditPlans", planId],
    queryFn: async () => {
      return getAuditPlanById(planId);
    },
  });

  const onActionCompleted: OnActionCompletedFn = (action) => {
    switch (action) {
      case "delete":
        navigate({ to: "/audit-plans" });
    }
  };

  if (isPending) {
    return (
      <div className="flex h-[calc(100vh-200px)] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading audit plan details...</p>
        </div>
      </div>
    );
  }

  if (!auditPlan) {
    return <AuditPlanNotFound />;
  }

  const config = statusConfig[auditPlan.state] || statusConfig["Draft"];
  const StatusIcon = config.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/audit-plans" })}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{auditPlan.title}</h1>
            <p className="text-muted-foreground">Audit Plan {auditPlan.referenceYear}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AuditPlanActions
            auditPlan={auditPlan}
            selectedActions={["defer", "delete"]}
            onActionCompleted={onActionCompleted}
          />
        </div>
      </div>

      {/* Overview Cards */}
      <AuditPlanOverview auditPlan={auditPlan} />

      {/* Details Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Audit Plan Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Audit Plan Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Plan Name
              </label>
              <p className="text-base font-medium mt-1">{auditPlan.title}</p>
            </div>

            <Separator />

            <div>
              <label className="text-sm font-medium text-muted-foreground">Year</label>
              <p className="text-base font-medium mt-1">{auditPlan.referenceYear}</p>
            </div>

            <Separator />

            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Badge
                  variant={config.badgeVariant}
                  className="flex items-center gap-1 w-fit"
                >
                  <StatusIcon className={`h-4 w-4 ${config.color}`} />
                  {auditPlan.state}
                </Badge>
              </div>
            </div>

            <Separator />

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Progress
              </label>
              <div className="mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {auditPlan.completionPercentage}%
                  </span>
                </div>
                <Progress value={auditPlan.completionPercentage} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline & Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline & Metadata
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Created Date
              </label>
              <p className="text-base font-medium mt-1">
                {format(new Date(auditPlan.insertedAt), "PPP")}
              </p>
            </div>

            <Separator />

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Last Modified
              </label>
              <p className="text-base font-medium mt-1">
                {format(new Date(auditPlan.lastChangeAt), "PPP")}
              </p>
            </div>

            <Separator />

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Created By
              </label>
              {/*<p className="text-base font-medium mt-1">{auditPlan.created_by_name}</p>*/}
            </div>

            <Separator />

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Last Modified By
              </label>
              <p className="text-base font-medium mt-1">{auditPlan.lastChangeBy}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Assignments */}
      <AssignmentsList assignments={auditPlan.assignments} auditPlan={auditPlan} />
    </div>
  );
}
